import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowRight, ArrowLeft, Check } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const LEVELS = [
  { id: "Experience Center", label: "Level 1 — Experience Center" },
  { id: "Experience & Development Hub", label: "Level 2 — Development Hub" },
  { id: "Destination Dealership & Resort", label: "Level 3 — Destination Resort" },
  { id: "Not sure yet", label: "Not sure yet — guide me" },
];
const RANGES = ["$150K", "$150K – $300K", "$300K – $500K", "$500K+"];
const TIMELINES = ["Immediately", "1 – 3 months", "3 – 6 months", "Just exploring"];

const empty = { full_name: "", email: "", phone: "", city: "", state: "", level: "", investment_range: "", timeline: "", message: "" };

const Field = ({ label, ...props }) => (
  <label className="block">
    <span className="text-[11px] uppercase tracking-[0.2em] text-white/40">{label}</span>
    <input
      {...props}
      className="mt-2 w-full bg-transparent border-b border-white/20 py-3 text-white placeholder-white/25 focus:border-oed-gold focus:outline-none focus:ring-0 transition-colors"
    />
  </label>
);

const Chip = ({ active, children, ...props }) => (
  <button
    type="button"
    {...props}
    className={`text-left px-5 py-3.5 border text-sm transition-colors ${
      active ? "border-oed-gold bg-oed-gold/10 text-white" : "border-white/15 text-white/60 hover:border-white/40"
    }`}
  >
    {children}
  </button>
);

export const ApplicationForm = ({ open, onClose }) => {
  const [step, setStep] = useState(0);
  const [data, setData] = useState(empty);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const steps = [
    {
      title: "About You",
      valid: data.full_name.trim() && /\S+@\S+\.\S+/.test(data.email) && data.phone.trim(),
      content: (
        <div className="space-y-8">
          <Field label="Full Name" data-testid="app-input-name" value={data.full_name} onChange={(e) => set("full_name", e.target.value)} placeholder="Jane Doe" />
          <div className="grid sm:grid-cols-2 gap-8">
            <Field label="Email" type="email" data-testid="app-input-email" value={data.email} onChange={(e) => set("email", e.target.value)} placeholder="you@email.com" />
            <Field label="Phone" data-testid="app-input-phone" value={data.phone} onChange={(e) => set("phone", e.target.value)} placeholder="(256) 268-7146" />
          </div>
          <div className="grid sm:grid-cols-2 gap-8">
            <Field label="City" data-testid="app-input-city" value={data.city} onChange={(e) => set("city", e.target.value)} placeholder="Talladega" />
            <Field label="State" data-testid="app-input-state" value={data.state} onChange={(e) => set("state", e.target.value)} placeholder="Alabama" />
          </div>
        </div>
      ),
    },
    {
      title: "Your Level",
      valid: !!data.level,
      content: (
        <div className="grid sm:grid-cols-2 gap-3">
          {LEVELS.map((l) => (
            <Chip key={l.id} active={data.level === l.id} data-testid={`app-level-${l.id.replace(/[^a-zA-Z]/g, "-").toLowerCase()}`} onClick={() => set("level", l.id)}>
              {l.label}
            </Chip>
          ))}
        </div>
      ),
    },
    {
      title: "Readiness",
      valid: true,
      content: (
        <div className="space-y-10">
          <div>
            <p className="text-[11px] uppercase tracking-[0.2em] text-white/40 mb-4">Investment Range</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {RANGES.map((r) => (
                <Chip key={r} active={data.investment_range === r} data-testid={`app-range-${r.replace(/[^a-zA-Z0-9]/g, "")}`} onClick={() => set("investment_range", r)}>{r}</Chip>
              ))}
            </div>
          </div>
          <div>
            <p className="text-[11px] uppercase tracking-[0.2em] text-white/40 mb-4">Timeline</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {TIMELINES.map((t) => (
                <Chip key={t} active={data.timeline === t} data-testid={`app-timeline-${t.replace(/[^a-zA-Z0-9]/g, "")}`} onClick={() => set("timeline", t)}>{t}</Chip>
              ))}
            </div>
          </div>
          <label className="block">
            <span className="text-[11px] uppercase tracking-[0.2em] text-white/40">Anything else? (optional)</span>
            <textarea
              data-testid="app-input-message"
              value={data.message}
              onChange={(e) => set("message", e.target.value)}
              rows={3}
              placeholder="Tell us about your goals, land, or questions…"
              className="mt-2 w-full bg-transparent border-b border-white/20 py-3 text-white placeholder-white/25 focus:border-oed-gold focus:outline-none resize-none transition-colors"
            />
          </label>
        </div>
      ),
    },
  ];

  const current = steps[step];

  const submit = async () => {
    setSubmitting(true);
    try {
      await api.post("/leads", data);
      setDone(true);
      toast.success("Application received. Our team will reach out shortly.");
    } catch (e) {
      toast.error("Something went wrong. Please try again or call 256-268-7146.");
    } finally {
      setSubmitting(false);
    }
  };

  const close = () => {
    onClose();
    setTimeout(() => { setStep(0); setData(empty); setDone(false); }, 300);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-8 bg-black/80 backdrop-blur-xl"
          onClick={close}
          data-testid="application-modal"
        >
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 20, scale: 0.98 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-2xl bg-oed-surface border border-white/10 max-h-[92vh] overflow-y-auto"
          >
            <button data-testid="app-close-button" onClick={close} className="absolute top-5 right-5 text-white/50 hover:text-white z-10">
              <X size={22} />
            </button>

            <div className="p-8 md:p-12">
              {done ? (
                <div className="py-12 text-center">
                  <div className="mx-auto w-16 h-16 border border-oed-gold flex items-center justify-center mb-8">
                    <Check size={28} className="text-oed-gold" />
                  </div>
                  <h3 className="font-display font-bold text-3xl tracking-tight">You're on the map.</h3>
                  <p className="mt-4 text-white/60 max-w-md mx-auto">
                    Thank you for applying to the OED Glampire Network. Our team will contact you shortly to discuss your territory.
                  </p>
                  <button data-testid="app-done-button" onClick={close} className="mt-8 bg-white text-black px-8 py-3.5 text-sm font-medium hover:bg-oed-gold transition-colors">
                    Done
                  </button>
                </div>
              ) : (
                <>
                  <p className="text-[11px] uppercase tracking-[0.3em] text-oed-gold">Dealership Application</p>
                  <h3 className="mt-3 font-display font-bold text-3xl md:text-4xl tracking-tight">{current.title}</h3>

                  <div className="mt-6 flex gap-2">
                    {steps.map((_, i) => (
                      <div key={i} className={`h-0.5 flex-1 transition-colors ${i <= step ? "bg-oed-gold" : "bg-white/15"}`} />
                    ))}
                  </div>
                  <p className="mt-3 text-xs text-white/40">Step {step + 1} of {steps.length}</p>

                  <div className="mt-10">
                    <AnimatePresence mode="wait">
                      <motion.div key={step} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.3 }}>
                        {current.content}
                      </motion.div>
                    </AnimatePresence>
                  </div>

                  <div className="mt-12 flex items-center justify-between">
                    <button
                      data-testid="app-back-button"
                      onClick={() => setStep((s) => Math.max(0, s - 1))}
                      disabled={step === 0}
                      className="inline-flex items-center gap-2 text-sm text-white/50 hover:text-white disabled:opacity-0 transition-colors"
                    >
                      <ArrowLeft size={16} /> Back
                    </button>

                    {step < steps.length - 1 ? (
                      <button
                        data-testid="app-next-button"
                        onClick={() => current.valid && setStep((s) => s + 1)}
                        disabled={!current.valid}
                        className="inline-flex items-center gap-2 bg-white text-black px-8 py-3.5 text-sm font-medium hover:bg-oed-gold transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                      >
                        Continue <ArrowRight size={16} />
                      </button>
                    ) : (
                      <button
                        data-testid="app-submit-button"
                        onClick={submit}
                        disabled={submitting}
                        className="inline-flex items-center gap-2 bg-oed-gold text-black px-8 py-3.5 text-sm font-medium hover:bg-oed-goldHover transition-colors disabled:opacity-50"
                      >
                        {submitting ? "Submitting…" : "Submit Application"} <ArrowRight size={16} />
                      </button>
                    )}
                  </div>
                </>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
